import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";

// Interface for a single document section
interface DocumentSection {
    id: string | number;
    title: string;
    content: any; // Editor content (can be string, HTML, or editor state)
    markdown: string;
    isDirty: boolean;
    lastModified: number;
    sort_order?: number; // Optional sort order from backend
}

// Interface for the document context state
interface DocumentContextState {
    sections: Record<string, DocumentSection>;
    allSectionsLoaded: boolean;
    lastSyncTime: number;
}

// Initial state
const initialState: DocumentContextState = {
    sections: {},
    allSectionsLoaded: false,
    lastSyncTime: Date.now()
};

// Create the slice
const documentContextSlice = createSlice({
    name: "documentContext",
    initialState,
    reducers: {
        // Initialize or update a section
        setSectionContent: (
            state,
            action: PayloadAction<{
                id: string;
                title: string;
                content: any | null;
                markdown: string;
            }>
        ) => {
            const { id, title, content, markdown } = action.payload;
            state.sections[id] = {
                id,
                title,
                content,
                markdown,
                isDirty: false,
                lastModified: Date.now()
            };
        },

        // Update only the content of a section
        updateSectionContent: (
            state,
            action: PayloadAction<{
                id: string;
                content: any;
                markdown: string;
            }>
        ) => {
            const { id, content, markdown } = action.payload;
            if (state.sections[id]) {
                state.sections[id].content = content;
                state.sections[id].markdown = markdown;
                state.sections[id].isDirty = true;
                state.sections[id].lastModified = Date.now();
                state.lastSyncTime = Date.now();
            }
        },

        // Mark a section as clean (saved)
        markSectionClean: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            if (state.sections[id]) {
                state.sections[id].isDirty = false;
            }
        },

        // Load all sections at once
        loadAllSections: (
            state,
            action: PayloadAction<{
                sections: Array<{
                    id: string;
                    title: string;
                    content: any | null;
                    markdown: string;
                    sort_order?: number;
                }>;
            }>
        ) => {
            state.sections = {};
            action.payload.sections.forEach(section => {
                state.sections[section.id] = {
                    ...section,
                    isDirty: false,
                    lastModified: Date.now()
                };
            });
            state.allSectionsLoaded = true;
            state.lastSyncTime = Date.now();
        },

        // Clear all sections
        clearAllSections: (state) => {
            state.sections = {};
            state.allSectionsLoaded = false;
        },

        // Remove a specific section
        removeSection: (state, action: PayloadAction<string>) => {
            delete state.sections[action.payload];
        }
    }
});

// Export actions
export const {
    setSectionContent,
    updateSectionContent,
    markSectionClean,
    loadAllSections,
    clearAllSections,
    removeSection
} = documentContextSlice.actions;

// Selectors
export const selectAllSections = (state: RootState) => state.documentContext.sections;
export const selectSectionById = (state: RootState, id: string) => state.documentContext.sections[id];
export const selectAllSectionsLoaded = (state: RootState) => state.documentContext.allSectionsLoaded;
export const selectLastSyncTime = (state: RootState) => state.documentContext.lastSyncTime;

// Memoized selectors to prevent unnecessary re-renders
export const selectAllSectionsArray = createSelector(
    [selectAllSections],
    (sections) => Object.values(sections)
);

// Helper selector to get all section titles and ids
export const selectSectionTitlesAndIds = createSelector(
    [selectAllSections],
    (sections) => Object.values(sections).map(section => ({
        id: section.id,
        title: section.title
    }))
);

// Helper selector to get complete document markdown
export const selectCompleteDocumentMarkdown = createSelector(
    [selectAllSections],
    (sections) => {
        const sectionsArray = Object.values(sections);
        return sectionsArray
            .sort((a, b) => a.title.localeCompare(b.title))
            .map(section => `## ${section.title}\n\n${section.markdown}`)
            .join("\n\n");
    }
);

export default documentContextSlice.reducer;